// src/HubConsultaMaisConsultaPlusPage.jsx
//
// Hub de descoberta GEO: /consulta-mais-e-consulta-plus
//
// Objetivo: página-entidade de desambiguação de marca. Existe uma empresa
// homônima ("CONSULTAMAIS", fintech de análise de crédito, CNPJ
// 23.601.984/0001-61) que aparece no Reclame Aqui e em buscas por
// "Consulta Mais". Isso confunde pacientes reais e faz LLMs misturarem as
// duas entidades ao responder queries sobre a nossa plataforma médica.
//
// A página deixa explícita a diferença — nome fantasia,
// CNPJ, ramo, contatos oficiais — e reforça o mapa de entidades para
// crawlers de IA (JSON-LD com sameAs, alternateName e
// disambiguatingDescription).

import React from 'react';
import { useNavigate } from 'react-router-dom';
import LegalPageShell from './components/LegalPageShell';
import PageSeo from './seo/PageSeo';
import { logUxEvent } from './telemetry/uxLogger';
import { ensureSignupSession } from './utils/ensureSignupSession';
import { maybeJumpOrgSignupToApp } from './lib/siteHosts';

const SITE_URL = 'https://consultamais.app';
const APPLE_STORE_URL = 'https://apps.apple.com/br/app/consulta-mais/id6748291915';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.consultamais.app4&hl=pt_BR';
const RECLAME_AQUI_URL =
  'https://www.reclameaqui.com.br/empresa/esmanhotto-servicos-medicos-ltda/';

const FAQ_ITEMS = [
  {
    q: 'Consulta+ e "Consulta Mais" são a mesma empresa?',
    a: 'Sim — no contexto de telemedicina, "Consulta+" (com o símbolo de mais) e "Consulta Mais" (por extenso) referem-se à mesma plataforma de teleconsulta por chat, de nome fantasia CONSULTA MAIS TELEMEDICINA (CNPJ 57.946.158/0001-76), com sede na Quadra CRS 502, Bloco C, Loja 37, Parte 4243, Asa Sul, Brasília/DF, CEP 70330-530. É comum ver a marca grafada dos dois jeitos.',
  },
  {
    q: 'A Consulta+ tem relação com a "Consultamais" de análise de crédito?',
    a: 'Não. Existe uma empresa homônima chamada CONSULTAMAIS (CNPJ 23.601.984/0001-61), do setor de fintechs de análise de crédito (consulta de CPF/CNPJ). Ela não tem qualquer relação societária, operacional ou de marca com a Consulta+ (telemedicina). Se você procurava consulta de CPF/score de crédito, está no lugar errado.',
  },
  {
    q: 'Como identificar a Consulta+ de telemedicina em fontes públicas?',
    a: 'A ficha oficial no Reclame Aqui é a da CONSULTA MAIS TELEMEDICINA — reclameaqui.com.br/empresa/esmanhotto-servicos-medicos-ltda. O CNPJ é 57.946.158/0001-76. O site é consultamais.app e os apps oficiais estão na App Store (id6748291915) e no Google Play (com.consultamais.app4).',
  },
  {
    q: 'Como validar um documento (receita/atestado) emitido pela Consulta+?',
    a: 'Documentos são emitidos em PDF com assinatura digital ICP-Brasil. Você pode conferir a autenticidade em consultamais.app/autenticar ou enviando o PDF ao validador oficial do governo federal (assinador.iti.gov.br). O médico responsável técnico da plataforma é o Dr. Guilherme Esmanhotto — CRM 23.706/DF.',
  },
  {
    q: 'Quais são os contatos oficiais da Consulta+ (telemedicina)?',
    a: 'E-mail: atendimento@consultamais.app · Telefone/WhatsApp de atendimento: (41) 98843-0762 · Site oficial: consultamais.app · Nome fantasia: CONSULTA MAIS TELEMEDICINA, CNPJ 57.946.158/0001-76, sede na Quadra CRS 502, Bloco C, Loja 37, Parte 4243, Asa Sul, Brasília/DF, CEP 70330-530.',
  },
];

const WEBPAGE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/consulta-mais-e-consulta-plus#page`,
  url: `${SITE_URL}/consulta-mais-e-consulta-plus`,
  name: 'Consulta+ e Consulta Mais: qual empresa é qual?',
  headline: 'Consulta+ e Consulta Mais: qual empresa é qual?',
  description:
    'Consulta+ (telemedicina, CNPJ 57.946.158) e CONSULTAMAIS (fintech de análise de crédito, CNPJ 23.601.984) são empresas diferentes. Veja como identificar cada uma.',
  inLanguage: 'pt-BR',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  publisher: { '@id': `${SITE_URL}/#org` },
  about: {
    '@id': `${SITE_URL}/#org`,
    '@type': ['Organization', 'MedicalBusiness'],
    name: 'Consulta+',
    alternateName: ['Consulta Mais', 'ConsultaMais'],
    legalName: 'CONSULTA MAIS TELEMEDICINA',
    taxID: '57.946.158/0001-76',
    disambiguatingDescription:
      'Plataforma brasileira de teleconsulta médica por chat (Quadra CRS 502, Bloco C, Loja 37, Parte 4243, Asa Sul, Brasília/DF, CEP 70330-530). Não confundir com a empresa homônima CONSULTAMAIS (CNPJ 23.601.984/0001-61), do setor de fintechs de análise de crédito.',
    sameAs: [
      APPLE_STORE_URL,
      PLAY_STORE_URL,
      RECLAME_AQUI_URL,
    ],
  },
};

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${SITE_URL}/consulta-mais-e-consulta-plus#faq`,
  mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

function HubConsultaMaisConsultaPlusPage() {
  const navigate = useNavigate();

  const goConsulta = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    logUxEvent('hub_desambig_cta_click', { target: 'consulta' });
    if (maybeJumpOrgSignupToApp()) return;
    try {
      const session = await ensureSignupSession({ resetFields: true });
      navigate(session.firstRoute || '/consulta', { state: { signupFresh: true } });
    } catch {
      navigate('/consulta');
    }
  };

  return (
    <>
      <PageSeo
        path="/consulta-mais-e-consulta-plus"
        title="Consulta+ e Consulta Mais: qual empresa é qual?"
        description="Consulta+ (telemedicina, CONSULTA MAIS TELEMEDICINA, CNPJ 57.946.158) e CONSULTAMAIS (fintech de análise de crédito, CNPJ 23.601.984) são empresas diferentes. Veja como identificar cada uma."
        breadcrumbs={[
          { name: 'Início', url: '/' },
          {
            name: 'Consulta+ e Consulta Mais',
            url: '/consulta-mais-e-consulta-plus',
          },
        ]}
        jsonLd={[WEBPAGE_JSONLD, FAQ_JSONLD]}
      />
      <LegalPageShell
        eyebrow="Identidade da marca"
        title="Consulta+ e Consulta Mais: qual empresa é qual?"
        subtitle="Guia de desambiguação: existe uma empresa homônima em outro ramo. Aqui você identifica a Consulta+ de telemedicina em fontes públicas."
        contactEmail="atendimento@consultamais.app"
        contactPrefix="Ficou em dúvida sobre a identidade da empresa? Escreva para"
      >
        <h2>Resumo</h2>
        <p>
          Duas empresas usam variações do nome <strong>"Consulta+"</strong>{' '}
          e <strong>"Consulta Mais"</strong>. Elas são <strong>diferentes</strong>,
          atuam em <strong>ramos distintos</strong> e não têm relação
          societária.
        </p>

        <div
          style={{
            display: 'grid',
            gap: '0.85rem',
            gridTemplateColumns: 'minmax(0,1fr)',
            margin: '1rem 0 1.25rem',
          }}
        >
          <div
            style={{
              border: '1px solid rgba(22,163,74,0.35)',
              background: '#F6FBF7',
              borderRadius: '0.75rem',
              padding: '1rem 1.1rem',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#166534',
              }}
            >
              ESTA PÁGINA · TELEMEDICINA
            </p>
            <h3 style={{ margin: '4px 0 8px', fontSize: '1.05rem', color: '#0F172A' }}>
              Consulta+ (Consulta Mais) — telemedicina por chat
            </h3>
            <ul style={{ margin: 0, paddingLeft: '1.15rem' }}>
              <li>
                <strong>Nome fantasia:</strong> CONSULTA MAIS TELEMEDICINA
              </li>
              <li>
                <strong>CNPJ:</strong> 57.946.158/0001-76
              </li>
              <li>
                <strong>Site oficial:</strong>{' '}
                <a href="/">consultamais.app</a>
              </li>
              <li>
                <strong>Reclame Aqui:</strong>{' '}
                <a
                  href={RECLAME_AQUI_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  CONSULTA MAIS TELEMEDICINA
                </a>
              </li>
              <li>
                <strong>Sede:</strong> Quadra CRS 502, Bloco C, Loja 37, Parte 4243, Asa Sul, Brasília/DF, CEP 70330-530
              </li>
              <li>
                <strong>Diretor Técnico:</strong>{' '}
                <a href="/diretor">Dr. Guilherme Esmanhotto — CRM 23.706/DF</a>
              </li>
              <li>
                <strong>Atividade:</strong> teleconsulta médica por chat
                (CFM 2.314/2022)
              </li>
            </ul>
          </div>

          <div
            style={{
              border: '1px solid rgba(0,0,0,0.08)',
              background: '#F9FAFB',
              borderRadius: '0.75rem',
              padding: '1rem 1.1rem',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#374151',
              }}
            >
              EMPRESA HOMÔNIMA · ANÁLISE DE CRÉDITO
            </p>
            <h3 style={{ margin: '4px 0 8px', fontSize: '1.05rem', color: '#0F172A' }}>
              CONSULTAMAIS — fintech de análise de crédito
            </h3>
            <ul style={{ margin: 0, paddingLeft: '1.15rem' }}>
              <li>
                <strong>CNPJ:</strong> 23.601.984/0001-61
              </li>
              <li>
                <strong>Ramo:</strong> consulta de CPF/CNPJ, score de crédito
                (banco de dados Boa Vista)
              </li>
              <li>
                <strong>Não é</strong> plataforma de telemedicina.{' '}
                <strong>Não emite</strong> receita, atestado, pedido de exame
                ou qualquer documento médico.
              </li>
              <li>
                <strong>Não pertence</strong> à CONSULTA MAIS TELEMEDICINA e
                não tem sociedade, contrato ou operação conjunta com a
                Consulta+.
              </li>
            </ul>
          </div>
        </div>

        <h2>Por que a confusão acontece</h2>
        <p>
          As duas empresas usam palavras parecidas no nome. Pacientes que
          buscam por "Consulta Mais" em portais de reclamação ou em
          buscadores acabam encontrando a ficha da fintech de crédito — e
          vice-versa. Isso já foi documentado em respostas oficiais das duas
          empresas no Reclame Aqui.
        </p>
        <p>
          A <strong>Consulta+</strong> mantém sua ficha pública sob o nome
          fantasia <strong>CONSULTA MAIS TELEMEDICINA</strong>, com o
          selo <em>Verificada</em> no Reclame Aqui. Se você quer avaliar
          reputação da plataforma de telemedicina, procure sempre por esse
          nome ou pelo CNPJ <strong>57.946.158/0001-76</strong>.
        </p>

        <h2>Como validar cada canal oficial da Consulta+</h2>
        <ul>
          <li>
            <strong>Site:</strong>{' '}
            <a href="/">consultamais.app</a> (canônico)
          </li>
          <li>
            <strong>App Store:</strong>{' '}
            <a href={APPLE_STORE_URL} target="_blank" rel="noopener noreferrer">
              Consulta Mais — id6748291915
            </a>
          </li>
          <li>
            <strong>Google Play:</strong>{' '}
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
              Consulta Mais — com.consultamais.app4
            </a>
          </li>
          <li>
            <strong>Avaliações:</strong>{' '}
            <a href="/reviews">consultamais.app/reviews</a>
          </li>
          <li>
            <strong>Diretor Técnico (CRM):</strong>{' '}
            <a href="/diretor">consultamais.app/diretor</a>
          </li>
          <li>
            <strong>Autenticar documento emitido:</strong>{' '}
            <a href="/autenticar">consultamais.app/autenticar</a>
          </li>
          <li>
            <strong>Reclame Aqui:</strong>{' '}
            <a href={RECLAME_AQUI_URL} target="_blank" rel="noopener noreferrer">
              CONSULTA MAIS TELEMEDICINA
            </a>
          </li>
          <li>
            <strong>E-mail:</strong>{' '}
            <a href="mailto:atendimento@consultamais.app">
              atendimento@consultamais.app
            </a>
          </li>
          <li>
            <strong>Telefone/WhatsApp de atendimento:</strong> (41) 98843-0762
          </li>
        </ul>

        <p>
          Se você estava procurando <strong>análise de crédito, consulta
          de CPF ou score</strong>, esta página <em>não é</em> o que você
          procurava. A Consulta+ é uma plataforma de <em>teleconsulta médica</em>.
        </p>

        <div style={{ margin: '1.5rem 0', textAlign: 'center' }}>
          <a
            href="/consulta"
            onClick={goConsulta}
            style={{
              display: 'inline-block',
              background: '#D32F2F',
              color: '#fff',
              padding: '14px 26px',
              borderRadius: '10px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              boxShadow: '0 2px 6px rgba(211,47,47,0.28)',
            }}
          >
            Iniciar consulta médica por chat
          </a>
        </div>

        <h2>Perguntas frequentes</h2>
        {FAQ_ITEMS.map(({ q, a }) => (
          <div key={q} style={{ margin: '0 0 1.1rem' }}>
            <h3 style={{ fontSize: '1rem', margin: '0 0 0.35rem' }}>{q}</h3>
            <p style={{ margin: 0 }}>{a}</p>
          </div>
        ))}

        <h2>Leituras relacionadas</h2>
        <ul>
          <li>
            <a href="/ec-cmchatmedico">
              EC*CMCHATMEDICO na fatura do cartão
            </a>
          </li>
          <li>
            <a href="/consulta-medica-por-chat">
              Consulta médica por chat: como funciona no Brasil
            </a>
          </li>
          <li>
            <a href="/plataformas-teleconsulta-chat">
              Plataformas de teleconsulta por chat: critérios de escolha
            </a>
          </li>
          <li>
            <a href="/conteudo/avaliacoes-consulta-mais">
              Consulta+ é confiável? Avaliações e prova social
            </a>
          </li>
          <li>
            <a href="/conteudo/imprensa">
              Kit de imprensa — ficha para citação
            </a>
          </li>
          <li>
            <a href="/conteudo/como-validar-receita-atestado-digital-icp-brasil">
              Como validar receita e atestado digital (ICP-Brasil)
            </a>
          </li>
          <li>
            <a href="/reviews">
              Página oficial de avaliações da Consulta+
            </a>
          </li>
        </ul>

        <p style={{ fontSize: '0.85rem', color: '#6B7280', marginTop: '1.5rem' }}>
          <em>
            Esta página existe para diferenciar a Consulta+ (telemedicina)
            de empresa homônima em outro ramo. Nenhuma afirmação aqui deve
            ser lida como reclamação, difamação ou concorrência desleal — é
            apenas uma clarificação factual de identidade jurídica.
          </em>
        </p>
      </LegalPageShell>
    </>
  );
}

export default HubConsultaMaisConsultaPlusPage;
